import NextImage from 'next/image';

import { cn } from '@/lib/utils';

import DecoroButton from '@/components/atoms/Button/DecoroButton';
import {
  ImageStrapiProps,
  transformCMSAssetToNextImageProps,
} from '@/components/atoms/Image/ImageStrapi';
import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';
import { transformFromCMSLink } from '@/components/atoms/LinkButton/utils';
import { Text } from '@/components/atoms/Text';

import { passPhTracker } from '@/integrations/trackingScripts/utils';

import { BENTO_BANNER_DEFAULT_IMAGE } from './constants';

export type BentoBannerProps = {
  title: string;
  cta: LinkButtonStrapiProps;
  image?: ImageStrapiProps;
  className?: string;
};

export default function BentoBanner({
  title,
  cta,
  image,
  className,
}: BentoBannerProps) {
  const { href, target } = transformFromCMSLink(cta);
  const cmsImage = image ? transformCMSAssetToNextImageProps(image) : null;
  const illustration = cmsImage?.src
    ? {
        src: cmsImage.src,
        alt: cmsImage.alt || '',
        width: cmsImage.width || BENTO_BANNER_DEFAULT_IMAGE.width,
        height: cmsImage.height || BENTO_BANNER_DEFAULT_IMAGE.height,
      }
    : { ...BENTO_BANNER_DEFAULT_IMAGE, alt: '' };

  const isLocalImage = illustration.src.startsWith('/');

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-xl border border-gray-dark-700 bg-gray-dark-950',
        className
      )}
      data-tracker-component='bento-banner'
    >
      <div
        className={cn(
          'relative flex bg-gradient-secondary-1-20',
          'flex-col',
          'md:h-14 md:flex-row md:items-stretch'
        )}
      >
        <div className='relative z-0 h-14 w-full shrink-0 overflow-hidden md:h-auto md:w-1/4 md:self-stretch'>
          <NextImage
            src={illustration.src}
            alt={illustration.alt}
            width={illustration.width}
            height={illustration.height}
            className='size-full object-cover'
            unoptimized={isLocalImage}
            aria-hidden={!illustration.alt || undefined}
          />
        </div>

        <div
          className={cn(
            'relative z-10 flex min-w-0 flex-1 flex-col items-stretch gap-3 p-4',
            'md:flex-row md:items-center md:justify-between md:gap-5 md:px-5 md:py-3'
          )}
        >
          {title && (
            <Text
              variant='body-m'
              classNames={{
                wrapper: 'font-serif min-w-0 flex-1 text-center md:text-left',
              }}
            >
              {title}
            </Text>
          )}

          {cta && (
            <DecoroButton
              as='a'
              href={href}
              target={target}
              variant='outlined'
              color='white'
              size='s'
              // keeps outline+white text; page dark mode would invert Decoro light skin
              {...{ theme: 'light' }}
              classNames={{ root: 'w-full shrink-0 md:w-auto' }}
              data-tracker-component='bento-banner-cta'
              {...passPhTracker(cta.phTracker)}
            >
              {cta.text}
            </DecoroButton>
          )}
        </div>
      </div>
    </div>
  );
}
